import { Controller } from "@hotwired/stimulus"
import Dropzone from "dropzone"

Dropzone.autoDiscover = false

export default class extends Controller {
  static values = {
    uploadUrl: String,
    paramName: String,
    acceptedFiles: String,
    disabled: Boolean,
    previewMode: Boolean,
    formSubmission: Boolean,
    enableCrop: Boolean,
    rowPreviews: Boolean,
    compact: Boolean,
    multiple: Boolean,
    maxFileSize: { type: Number, default: 10 },
    maxFiles: Number,
    removeLabel: String
  }
  // `preview` and `fileInput` are optional: supply them when the preview chips
  // belong in a specific slot and the picker opens from your own button
  // (message composers) rather than the whole dropzone being clickable.
  static targets = ["dropzone", "preview", "fileInput"]

  connect() {
    this.parser = new DOMParser()
    this.fileCount ??= 0
    this.initializeDropzone()
  }

  disconnect() {
    if (this.formSubmissionValue && this.element.closest("[data-turbo-permanent]")) return

    if (!this.hasPreviewTarget && this.previewContainer && this.previewContainer.parentNode) {
      this.previewContainer.parentNode.removeChild(this.previewContainer)
    }
    this.dropzone?.destroy()
  }

  initializeDropzone() {
    if (this.hasDisabledValue && this.disabledValue) {
      return
    }

    // For form submission mode, we don't need a real upload URL
    const uploadUrl = this.hasFormSubmissionValue && this.formSubmissionValue
      ? '/fake-upload-url'
      : this.uploadUrlValue

    const isSingleFile = !this.isMultiple
    this.isSingleFile = isSingleFile

    // Turbo reconnects on DOM nodes that survived the visit. Adopt the live
    // instance rather than rebuilding: destroy() cancels uploads still in
    // flight and fires removedfile handlers bound to the old controller.
    if (this.dropzoneTarget.dropzone) {
      this.dropzone = this.dropzoneTarget.dropzone
      return
    }

    this.dropzone = new Dropzone(this.dropzoneTarget, {
      url: uploadUrl,
      paramName: this.paramNameValue,
      maxFilesize: this.maxFileSizeValue,
      acceptedFiles: this.acceptedFilesValue,
      maxFiles: isSingleFile ? 1 : (this.hasMaxFilesValue ? this.maxFilesValue : null), // Limit to 1 file for single file uploads
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
        'Accept': 'text/vnd.turbo-stream.html'
      },
      previewsContainer: false,
      clickable: this.hasFileInputTarget ? false : this.dropzoneTarget,
      addRemoveLinks: false,
      autoProcessQueue: !this.formSubmissionValue  // Don't auto-upload in form submission mode
    })

    if (this.previewModeValue) {
      this.dropzone.on("addedfile", (file) => {
        this.flashMessage('info', 'File not uploaded, because this is a preview.')
      })
      return;
    }

    if (this.hasFormSubmissionValue && this.formSubmissionValue) {
      this.setupFormSubmissionHandlers()
    } else {
      this.setupUploadHandlers()
    }
  }

  setupUploadHandlers() {
    this.dropzone.on("addedfile", (file) => {
      // Create and position progress meter
      const progressElement = document.createElement('progress')
      progressElement.className = 'progress progress-primary w-[50%] max-w-[300px]'
      progressElement.value = 0
      progressElement.max = 100
      progressElement.style.position = 'absolute'
      progressElement.style.bottom = '10%'
      progressElement.style.left = '50%'
      progressElement.style.transform = 'translate(-50%, 0)'
      progressElement.style.opacity = '0.8'

      // Store reference to progress element on file object
      file.progressElement = progressElement

      // Add to dropzone
      this.dropzoneTarget.style.position = 'relative'
      this.dropzoneTarget.appendChild(progressElement)
    })

    this.dropzone.on("uploadprogress", (file, progress) => {
      if (file.progressElement) {
        file.progressElement.value = progress
      }
    })

    this.dropzone.on("complete", (file) => {
      // Remove progress element when upload completes
      if (file.progressElement) {
        file.progressElement.remove()
      }
    })


    this.dropzone.on("success", async (file, response) => {
      // Handle Turbo Stream response
      const streamElements = this.parser.parseFromString(response, 'text/html').querySelectorAll('turbo-stream')

      streamElements.forEach(streamElement => {
        Turbo.renderStreamMessage(streamElement.outerHTML)
      })

      this.dropzoneTarget.dispatchEvent(
        new CustomEvent("dropzone:success")
      )
    })

    this.dropzone.on("error", (file, errorMessage) => {
      const streamElements = this.parser.parseFromString(errorMessage, 'text/html').querySelectorAll('turbo-stream')

      if (streamElements.length > 0) {
        streamElements.forEach(streamElement => {
          Turbo.renderStreamMessage(streamElement.outerHTML)
        })
      } else {
        this.flashMessage('error', errorMessage.error || errorMessage)
      }

      this.dropzoneTarget.dispatchEvent(
        new CustomEvent("dropzone:error")
      )
    })


  }

  setupFormSubmissionHandlers() {
    // Create container for file previews if it doesn't exist
    this.ensurePreviewContainer()
    
    // Intercept form submission to ensure files are included
    const form = this.element.closest('form')
    if (form) {
      form.addEventListener('submit', (e) => {
        // Ensure all files are properly attached to form inputs
        this.dropzone.files.forEach((file) => {
          if (file.fileInput && file.fileInput.parentNode === form) {
            // File input is already in form, should work
          } else if (file.fileInput) {
            // Re-add if it was removed somehow
            form.appendChild(file.fileInput)
          } else {
            // Create input if it doesn't exist
            const fileInput = document.createElement('input')
            fileInput.type = 'file'
            fileInput.name = this.fileParamName
            fileInput.style.display = 'none'
            fileInput.files = this.createFileList(file)
            form.appendChild(fileInput)
            file.fileInput = fileInput
          }
        })
      }, { capture: true })
    }
    
    this.dropzone.on("addedfile", (file) => {
      // Check if cropping is enabled for this dropzone
      const cropEnabled = this.hasEnableCropValue && this.enableCropValue
      
      // Check if file is an image that needs cropping (not SVG)
      // Only crop if cropping is enabled and file is not already cropped
      const isImageNeedingCrop = cropEnabled && file.type && file.type.startsWith('image/') && !file.type.includes('svg') && !file.alreadyCropped
      
      // If file needs cropping, prevent normal processing and dispatch event for cropper
      if (isImageNeedingCrop) {
        // Mark file as needing crop so we don't process it normally
        file.needsCrop = true
        
        // Dispatch event for image cropper to handle
        // Use setTimeout to ensure dropzone has finished processing the file
        setTimeout(() => {
          this.dropzoneTarget.dispatchEvent(
            new CustomEvent("dropzone:image-needs-crop", { 
              detail: { file, dropzone: this.dropzone, dropzoneController: this },
              bubbles: true
            })
          )
        }, 10)
        return
      }
      
      this.processFile(file)
    })

    this.dropzone.on("error", (file, message) => {
      this.dropzone.removeFile(file)
      const event = this.dispatch("rejected", { detail: { message } })
      if (!event.defaultPrevented) {
        this.rejectionNotice?.remove()
        this.rejectionNotice = document.createElement("p")
        this.rejectionNotice.className = "alert alert-error mt-2"
        this.rejectionNotice.setAttribute("role", "alert")
        this.rejectionNotice.textContent = message
        this.element.appendChild(this.rejectionNotice)
      }
    })

    this.dropzone.on("removedfile", (file) => {
      if (file.fileInput) this.fileCount--
      if (file.previewElement && file.previewElement.parentNode) {
        file.previewElement.parentNode.removeChild(file.previewElement)
      }
      if (file.fileInput && file.fileInput.parentNode) {
        file.fileInput.parentNode.removeChild(file.fileInput)
      }
      this.updatePreviewContainerVisibility()
      
      // Clean up file references
      delete file.previewElement
      delete file.fileInput
      delete file.previewContainer
      this.dispatch("files-changed", { detail: { count: this.fileCount } })
    })
  }

  processFile(file) {
    this.rejectionNotice?.remove()
    this.fileCount++
    
    // For single file uploads, remove any previous files
    if (this.isSingleFile && this.dropzone.files.length > 1) {
      // Remove the first file (old one) when adding a new one
      const oldFile = this.dropzone.files[0]
      if (oldFile && oldFile !== file) {
        this.dropzone.removeFile(oldFile)
      }
    }
    
    // Create a hidden file input that will be submitted with the form
    const fileInput = document.createElement('input')
    fileInput.type = 'file'
    fileInput.name = this.fileParamName
    fileInput.style.display = 'none'
    fileInput.files = this.createFileList(file)

    // Store reference to input on file object for later removal
    file.fileInput = fileInput
    
    // Add to the form (find the closest form element)
    const form = this.element.closest('form')
    if (form) {
      form.appendChild(fileInput)
    }
    
    this.dispatch("files-changed", { detail: { count: this.fileCount } })

    // Row previews: one full-width document row per file, regardless of type
    if (this.rowPreviewsValue) {
      const fileElement = this.buildRowPreview(file)
      file.previewElement = fileElement
      this.previewContainer.appendChild(fileElement)
      this.previewContainer.style.display = 'flex'
      this.dropzoneTarget.dispatchEvent(
        new CustomEvent("dropzone:file-added", { detail: { file, fileCount: this.fileCount } })
      )
      return
    }

    // Compact chips: a thumbnail or a small file card, sized for a message
    // composer where a 128px preview would swamp the editor.
    if (this.compactValue) {
      const fileElement = this.buildCompactPreview(file)
      file.previewElement = fileElement
      this.previewContainer.appendChild(fileElement)
      this.previewContainer.style.display = 'flex'
      return
    }

    // Check if file is an image
    const isImage = file.type && file.type.startsWith('image/')
    
    // Create visual feedback element
    const fileElement = document.createElement('span')
    fileElement.className = 'bg-gray-100 rounded-lg p-2 mr-2 flex items-center justify-between text-xs'
    
    if (isImage) {
      // For images, show thumbnail preview
      fileElement.className = 'bg-gray-100 rounded-lg p-2 mr-2 flex flex-col items-center gap-2 relative'
      fileElement.style.maxWidth = '150px'
      
      // Create image preview
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = document.createElement('img')
        img.src = e.target.result
        img.className = 'w-32 h-32 object-cover rounded border'
        img.style.maxWidth = '128px'
        img.style.maxHeight = '128px'
        
        const fileName = document.createElement('span')
        fileName.className = 'text-xs text-gray-700 truncate w-full text-center'
        fileName.textContent = file.name
        
        const removeButton = document.createElement('button')
        removeButton.type = 'button'
        removeButton.className = 'absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-colors'
        removeButton.innerHTML = `
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        `
        
        removeButton.addEventListener('click', () => {
          this.dropzone.removeFile(file)
        })
        
        fileElement.appendChild(img)
        fileElement.appendChild(fileName)
        fileElement.appendChild(removeButton)
        
        // Add to preview container now that image is loaded
        if (file.previewContainer) {
          file.previewContainer.appendChild(fileElement)
          file.previewContainer.style.display = 'flex'
        }
      }
      reader.readAsDataURL(file)
    } else {
      // For non-images, show file icon and name
      fileElement.className = 'bg-gray-100 rounded-lg p-2 mr-2 flex items-center justify-between text-xs max-w-[200px]'
      fileElement.innerHTML = `
        <div class="flex items-center min-w-0">
          <svg class="w-4 h-4 text-gray-500 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
          <span class="text-xs text-gray-700 truncate">${file.name}</span>
        </div>
        <button type="button" class="text-red-500 hover:text-red-700 ml-1 flex-shrink-0">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      `
      
      // Add remove functionality for non-image files
      const removeButton = fileElement.querySelector('button')
      removeButton.addEventListener('click', () => {
        this.dropzone.removeFile(file)
      })
    }
    
    // Store reference to visual element on file object
    file.previewElement = fileElement
    
    // Add visual element to the preview container
    if (isImage) {
      // For images, the preview is added in reader.onload callback
      // Store reference to preview container on file for later
      file.previewContainer = this.previewContainer
    } else {
      // For non-images, add immediately
      this.previewContainer.appendChild(fileElement)
      this.previewContainer.style.display = 'flex'
    }
    
    // Dispatch success event for consistency
    this.dropzoneTarget.dispatchEvent(
      new CustomEvent("dropzone:file-added", { detail: { file, fileCount: this.fileCount } })
    )
  }

  // Referral-style document row: file icon + name + remove button
  buildRowPreview(file) {
    const row = document.createElement('div')
    row.className = 'flex w-full items-center gap-2 border-b border-slate-200 last:border-b-0 py-3 text-sm'
    row.innerHTML = `
      <svg class="w-5 h-5 text-slate-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
      </svg>
      <span class="min-w-0 flex-1 truncate text-left text-slate-900"></span>
      <button type="button" class="text-red-500 hover:text-red-700 flex-shrink-0">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>`
    row.querySelector('span').textContent = file.name
    row.querySelector('button').addEventListener('click', () => this.dropzone.removeFile(file))
    return row
  }

  // Helper method to create a FileList from a single file
  createFileList(file) {
    const dt = new DataTransfer()
    dt.items.add(file)
    return dt.files
  }

  // Ensure preview container exists for horizontal layout
  // Multi-file is inferred from a trailing [] on the param name, which is how
  // existing call sites declare it. That inference is easy to trip over —
  // `record[documents]` is ordinary Rails nesting, not a single-file signal —
  // so `multiple` overrides it, and the [] is appended for you either way.
  get isMultiple() {
    return this.hasMultipleValue ? this.multipleValue : this.paramNameValue.endsWith('[]')
  }

  get fileParamName() {
    if (!this.isMultiple) return this.paramNameValue

    return this.paramNameValue.endsWith('[]') ? this.paramNameValue : `${this.paramNameValue}[]`
  }

  // Actions for an external picker button: click->dropzone#triggerFileInput
  // on the button, change->dropzone#handleFileSelect on the hidden input.
  triggerFileInput() {
    this.fileInputTarget.click()
  }

  handleFileSelect(event) {
    Array.from(event.target.files).forEach((file) => this.dropzone.addFile(file))
    event.target.value = "" // so the same file can be picked again
  }

  handlePaste(event) {
    const images = Array.from(event.clipboardData?.items || [])
      .filter((item) => item.type.startsWith("image/"))
      .map((item) => item.getAsFile())
      .filter(Boolean)
    if (!this.dropzone || images.length === 0) return

    event.preventDefault()
    images.forEach((file) => this.dropzone.addFile(file))
  }

  clear() {
    this.dropzone?.removeAllFiles(true)
  }

  buildCompactPreview(file) {
    const wrapper = document.createElement('div')
    wrapper.className = 'relative group'
    const extension = (file.name.split('.').pop() || 'file').toUpperCase()

    if (file.type && file.type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onload = (e) => {
        wrapper.insertAdjacentHTML('afterbegin',
          `<img src="${e.target.result}" class="w-14 h-14 object-cover rounded-lg border my-2">`)
      }
      reader.readAsDataURL(file)
    } else {
      wrapper.innerHTML = `
        <div class="bg-base-50 border rounded-lg p-2 flex items-center gap-2 min-w-[180px] max-w-[200px] h-14 my-2">
          <div class="bg-slate-800 rounded flex items-center justify-center w-8 h-8 flex-shrink-0">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
          </div>
          <div class="flex flex-col flex-1 min-w-0">
            <div class="text-sm text-black truncate" data-name></div>
            <div class="text-xs text-gray-500" data-extension></div>
          </div>
        </div>`
      // textContent, never interpolation: the filename is attacker-chosen
      // text and would otherwise be parsed as markup.
      const nameEl = wrapper.querySelector('[data-name]')
      nameEl.textContent = file.name
      nameEl.title = file.name
      wrapper.querySelector('[data-extension]').textContent = extension
    }

    const remove = document.createElement('button')
    remove.type = 'button'
    if (this.hasRemoveLabelValue) remove.setAttribute('aria-label', this.removeLabelValue)
    remove.className = 'absolute top-0 -right-2 bg-slate-500 hover:bg-slate-600 text-white rounded-full p-1'
    remove.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="w-2.5 h-2.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>'
    remove.addEventListener('click', (e) => {
      e.stopPropagation()
      this.dropzone.removeFile(file)
    })
    wrapper.appendChild(remove)

    return wrapper
  }

  ensurePreviewContainer() {
    if (this.hasPreviewTarget) {
      this.previewContainer = this.previewTarget
      return
    }

    if (!this.previewContainer) {
      this.previewContainer = document.createElement('div')
      this.previewContainer.className = this.rowPreviewsValue ? 'flex flex-col mb-2' : 'flex flex-wrap gap-2 mb-4'
      this.previewContainer.style.display = 'none' // Hidden by default, shown when files are added
      
      // Insert preview container BEFORE the dropzone, not containing it
      this.dropzoneTarget.parentNode.insertBefore(this.previewContainer, this.dropzoneTarget)
    }
  }

  // Show/hide preview container based on file count
  updatePreviewContainerVisibility() {
    if (this.previewContainer) {
      if (this.fileCount === 0) {
        this.previewContainer.style.display = 'none'
      } else {
        this.previewContainer.style.display = 'flex'
      }
    }
  }

  flashMessage(type, message) {
    const errorHtml = `
    <turbo-stream action="prepend" target="flash">
      <template>
        <div x-data="{ show: true }" x-init="setTimeout(() => show = false, 3500)" x-show="show" x-transition:leave="transition ease-in duration-1000"
        x-transition:leave-start="opacity-100" x-transition:leave-end="opacity-0" @transitionend="if (!show) $el.remove()" class="alert alert-${type}">
          ${message}
        </div>
      </template>
    </turbo-stream>
  `
    Turbo.renderStreamMessage(errorHtml)
  }
}
